import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart, ScatterChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Scatter, Cell, PieChart, Pie, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, ReferenceLine,
} from 'recharts';
import { METHOD_META } from '../../utils/anomalyMethods';

const C = {
  blue: '#2563eb', green: '#16a34a', red: '#dc2626', orange: '#d97706', purple: '#7c3aed',
  text: '#1e293b', muted: '#64748b', border: '#e2e8f0', bg: '#f8fafc', card: '#fff',
  palette: ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626'],
};

const fmt = (n, d = 1) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
  return Number(n).toLocaleString('tr-TR', { maximumFractionDigits: d });
};

const fmtDonem = (d) => {
  const s = String(d || '');
  if (s.length >= 6) return `${s.slice(4, 6)}.${s.slice(0, 4)}`;
  return s || '-';
};

const shortName = (s, n = 18) => {
  const t = String(s || '');
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

const ChartCard = ({ title, sub, children, height = 300 }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: 14, marginBottom: 14,
  }}>
    <div style={{ fontWeight: 800, fontSize: 13, color: C.text }}>{title}</div>
    {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3, marginBottom: 8 }}>{sub}</div>}
    <div style={{ width: '100%', height }}>{children}</div>
  </div>
);

const AdminRaporCharts = ({
  parks = [],
  selectedPark,
  selectedMethod = 'zscore',
  onSelectPark,
  onSelectMethod,
}) => {
  const [localPark, setLocalPark] = useState(null);
  const activeNo = localPark || selectedPark || parks[0]?.aboneno;
  const park = parks.find((p) => p.aboneno === activeNo) || parks[0];
  const methodId = selectedMethod || 'zscore';

  useEffect(() => {
    if (selectedPark) setLocalPark(selectedPark);
  }, [selectedPark]);

  const pickPark = (no) => {
    setLocalPark(no);
    onSelectPark?.(no);
  };

  const parkSeries = useMemo(() => {
    if (!park?.series) return [];
    const mean = park.stats?.mean || 0;
    let cum = 0;
    return park.series.map((s) => {
      cum += s.m3;
      return {
        donem: fmtDonem(s.donem),
        donemRaw: String(s.donem),
        m3: s.m3,
        ortalama: mean,
        kumulatif: cum,
      };
    });
  }, [park]);

  const methodOverlay = useMemo(() => {
    const m = park?.methods?.find((x) => x.id === methodId);
    if (!m?.points?.length) return [];
    return m.points.map((p) => ({
      donem: p.donemLabel || fmtDonem(p.donem),
      m3: p.m3,
      anomali: p.isAnomaly ? p.m3 : null,
      normal: p.isAnomaly ? null : p.m3,
      zScore: p.zScore,
      anomalyScore: p.anomalyScore,
      lof: p.lof,
      forecast: p.forecast,
      upper: p.upper,
      lower: p.lower,
      isAnomaly: p.isAnomaly,
    }));
  }, [park, methodId]);

  const monthlyAvg = useMemo(() => {
    const buckets = Array.from({ length: 12 }, (_, i) => ({ ay: String(i + 1).padStart(2, '0'), toplam: 0, n: 0 }));
    (park?.series || []).forEach((s) => {
      const m = parseInt(String(s.donem).slice(4, 6), 10);
      if (m >= 1 && m <= 12) {
        buckets[m - 1].toplam += s.m3;
        buckets[m - 1].n += 1;
      }
    });
    return buckets.map((b) => ({
      ay: b.ay,
      ort: b.n ? b.toplam / b.n : 0,
      n: b.n,
    }));
  }, [park]);

  const summaryBars = useMemo(() => parks.map((p, i) => ({
    name: shortName(p.abone_adi, 14),
    full: p.abone_adi,
    aboneno: p.aboneno,
    ortalama: Math.round(p.stats?.mean || 0),
    toplam: Math.round(p.stats?.total || 0),
    donem: p.stats?.n || 0,
    std: Math.round(p.stats?.std || 0),
    fill: C.palette[i % C.palette.length],
  })), [parks]);

  const methodMatrixBars = useMemo(() => METHOD_META.map((meta) => {
    const row = { metod: shortName(meta.name, 12), full: meta.name, id: meta.id };
    parks.forEach((p, i) => {
      const m = (p.methods || []).find((x) => x.id === meta.id);
      row[`p${i}`] = m?.stats?.anomalyCount || 0;
    });
    row.toplam = parks.reduce((s, p) => {
      const m = (p.methods || []).find((x) => x.id === meta.id);
      return s + (m?.stats?.anomalyCount || 0);
    }, 0);
    return row;
  }), [parks]);

  const parkMethodBars = useMemo(() => {
    if (!park?.methods) return [];
    return park.methods.map((m) => ({
      name: shortName(m.name, 10),
      full: m.name,
      id: m.id,
      anomali: m.stats?.anomalyCount || 0,
      kategori: m.category,
    }));
  }, [park]);

  const multiSeries = useMemo(() => {
    const set = new Set();
    parks.forEach((p) => (p.series || []).forEach((s) => set.add(String(s.donem))));
    return [...set].sort().map((donem) => {
      const row = { donem: fmtDonem(donem) };
      parks.forEach((p, i) => {
        const hit = (p.series || []).find((s) => String(s.donem) === donem);
        row[`p${i}`] = hit ? hit.m3 : null;
      });
      return row;
    });
  }, [parks]);

  /** Ölçek farkı büyük parklar için normalize (ilk değeri 100) */
  const multiIndexed = useMemo(() => {
    const bases = parks.map((p) => {
      const first = (p.series || []).find((s) => s.m3 > 0);
      return first?.m3 || 1;
    });
    const set = new Set();
    parks.forEach((p) => (p.series || []).forEach((s) => set.add(String(s.donem))));
    return [...set].sort().map((donem) => {
      const row = { donem: fmtDonem(donem) };
      parks.forEach((p, i) => {
        const hit = (p.series || []).find((s) => String(s.donem) === donem);
        row[`p${i}`] = hit ? (hit.m3 / bases[i]) * 100 : null;
      });
      return row;
    });
  }, [parks]);

  const pieTotals = useMemo(() => parks.map((p, i) => ({
    name: shortName(p.abone_adi, 16),
    value: Math.max(0, Math.round(p.stats?.total || 0)),
    fill: C.palette[i % C.palette.length],
  })), [parks]);

  const histData = useMemo(() => {
    const vals = (park?.series || []).map((s) => s.m3).filter((v) => v != null);
    if (!vals.length) return [];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const bins = 8;
    const step = (max - min) / bins || 1;
    const arr = Array.from({ length: bins }, (_, i) => ({
      aralik: `${fmt(min + i * step, 0)}–${fmt(min + (i + 1) * step, 0)}`,
      adet: 0,
    }));
    vals.forEach((v) => {
      let idx = Math.floor((v - min) / step);
      if (idx >= bins) idx = bins - 1;
      if (idx < 0) idx = 0;
      arr[idx].adet += 1;
    });
    return arr;
  }, [park]);

  const radarMethods = useMemo(() => {
    if (!park?.methods) return [];
    return park.methods.map((m) => ({
      metod: shortName(m.name, 10),
      anomali: m.stats?.anomalyCount || 0,
    }));
  }, [park]);

  const gpsParks = parks.filter((p) => p.location?.hasGps);

  if (!parks.length) return null;

  return (
    <div style={{ margin: '28px 0' }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Grafikler — tüm tüketim & analiz</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
        Gerçek ASKİ m³ serisi · 13 metod anomali çıktıları · park karşılaştırmaları (mock yok)
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {parks.map((p, i) => (
          <button
            key={p.aboneno}
            type="button"
            onClick={() => pickPark(p.aboneno)}
            style={{
              padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              border: activeNo === p.aboneno ? `2px solid ${C.palette[i % 5]}` : `1px solid ${C.border}`,
              background: activeNo === p.aboneno ? '#eff6ff' : C.card,
              color: C.text,
              maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title={p.abone_adi}
          >
            {shortName(p.abone_adi, 22)}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>
        <ChartCard
          title={`1) Dönemsel tüketim — ${shortName(park?.abone_adi, 40)}`}
          sub={`${park?.aboneno} · N=${park?.stats?.n} · ort ${fmt(park?.stats?.mean)} m³`}
          height={320}
        >
          <ResponsiveContainer>
            <ComposedChart data={parkSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={18} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${fmt(v)} m³`} />
              <Legend />
              <Area type="monotone" dataKey="m3" name="Tüketim" fill="#dbeafe" stroke={C.blue} strokeWidth={2} />
              <Line type="monotone" dataKey="ortalama" name="Ortalama" stroke={C.orange} strokeDasharray="4 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={`2) Seçili metod anomali — ${METHOD_META.find((m) => m.id === methodId)?.name || methodId}`}
          sub="Kırmızı noktalar = anomali dönemleri (gerçek skor)"
          height={320}
        >
          <ResponsiveContainer>
            <ComposedChart data={methodOverlay}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={18} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="m3" name="m³" stroke={C.blue} strokeWidth={2} dot={false} />
              {methodOverlay.some((r) => r.upper != null) && (
                <Line type="monotone" dataKey="upper" name="Üst eşik" stroke={C.red} strokeDasharray="3 3" dot={false} />
              )}
              {methodOverlay.some((r) => r.lower != null) && (
                <Line type="monotone" dataKey="lower" name="Alt eşik" stroke={C.green} strokeDasharray="3 3" dot={false} />
              )}
              {methodOverlay.some((r) => r.forecast != null) && (
                <Line type="monotone" dataKey="forecast" name="Tahmin" stroke={C.purple} strokeDasharray="4 4" dot={false} />
              )}
              <Scatter dataKey="anomali" name="Anomali" fill={C.red} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {METHOD_META.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onSelectMethod?.(m.id)}
            style={{
              padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
              border: methodId === m.id ? `1px solid ${C.blue}` : `1px solid ${C.border}`,
              background: methodId === m.id ? '#dbeafe' : C.card,
              color: methodId === m.id ? C.blue : C.muted,
            }}
          >
            {m.name}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ChartCard title="3) Kümülatif tüketim" sub="Seçili park — dönemler toplamı" height={260}>
          <ResponsiveContainer>
            <AreaChart data={parkSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${fmt(v)} m³`} />
              <Area type="monotone" dataKey="kumulatif" name="Kümülatif m³" stroke={C.purple} fill="#ede9fe" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="4) Aylık ortalama (mevsim)" sub="Ay bazında ortalama m³" height={260}>
          <ResponsiveContainer>
            <BarChart data={monthlyAvg}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="ay" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${fmt(v)} m³`} />
              <Bar dataKey="ort" name="Ay ort. m³" fill={C.green} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <ChartCard title="5) Park ortalama m³" height={260}>
          <ResponsiveContainer>
            <BarChart data={summaryBars} layout="vertical" margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v, _n, ctx) => [`${fmt(v)} m³`, ctx?.payload?.full]} />
              <Bar dataKey="ortalama" name="Ortalama" radius={[0, 4, 4, 0]}>
                {summaryBars.map((e) => <Cell key={e.aboneno} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="6) Park toplam m³" height={260}>
          <ResponsiveContainer>
            <BarChart data={summaryBars}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, _n, ctx) => [`${fmt(v)} m³`, ctx?.payload?.full]} />
              <Bar dataKey="toplam" name="Toplam" radius={[4, 4, 0, 0]}>
                {summaryBars.map((e) => <Cell key={e.aboneno} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="7) Toplam tüketim payı" height={260}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pieTotals} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {pieTotals.map((e) => <Cell key={e.name} fill={e.fill} />)}
              </Pie>
              <Tooltip formatter={(v) => `${fmt(v)} m³`} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard
        title="8) Tüm parklar — ham m³ zaman serisi"
        sub="Ölçek farkı büyükse aşağıdaki endeks grafiğine bakın"
        height={340}
      >
        <ResponsiveContainer>
          <LineChart data={multiSeries}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={22} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            {parks.map((p, i) => (
              <Line
                key={p.aboneno}
                type="monotone"
                dataKey={`p${i}`}
                name={shortName(p.abone_adi, 18)}
                stroke={C.palette[i % 5]}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="9) Normalize endeks (ilk dönem = 100)"
        sub="Farklı ölçekteki parkları birlikte izlemek için"
        height={300}
      >
        <ResponsiveContainer>
          <LineChart data={multiIndexed}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="donem" tick={{ fontSize: 10 }} minTickGap={22} />
            <YAxis tick={{ fontSize: 10 }} />
            <ReferenceLine y={100} stroke={C.muted} strokeDasharray="3 3" />
            <Tooltip />
            <Legend />
            {parks.map((p, i) => (
              <Line
                key={p.aboneno}
                type="monotone"
                dataKey={`p${i}`}
                name={shortName(p.abone_adi, 18)}
                stroke={C.palette[i % 5]}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ChartCard
          title={`10) Seçili park — 13 metod anomali sayısı`}
          sub={shortName(park?.abone_adi, 50)}
          height={300}
        >
          <ResponsiveContainer>
            <BarChart data={parkMethodBars} margin={{ bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={55} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, _n, ctx) => [v, ctx?.payload?.full]} />
              <Bar
                dataKey="anomali"
                name="Anomali"
                fill={C.red}
                radius={[4, 4, 0, 0]}
                onClick={(d) => d?.id && onSelectMethod?.(d.id)}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="11) Metod radar (anomali yoğunluğu)" height={300}>
          <ResponsiveContainer>
            <RadarChart data={radarMethods}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metod" tick={{ fontSize: 9 }} />
              <PolarRadiusAxis tick={{ fontSize: 9 }} />
              <Radar name="Anomali" dataKey="anomali" stroke={C.red} fill={C.red} fillOpacity={0.25} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard
        title="12) Metot × Park anomali matrisi (bar)"
        sub="Her metod için 5 parkın anomali sayısı"
        height={360}
      >
        <ResponsiveContainer>
          <BarChart data={methodMatrixBars} margin={{ bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="metod" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={55} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            {parks.map((p, i) => (
              <Bar
                key={p.aboneno}
                dataKey={`p${i}`}
                name={shortName(p.abone_adi, 14)}
                fill={C.palette[i % 5]}
                stackId={undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ChartCard title="13) Tüketim dağılımı (histogram)" sub="Seçili park m³ kutuları" height={260}>
          <ResponsiveContainer>
            <BarChart data={histData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="aralik" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="adet" name="Dönem adedi" fill={C.blue} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="14) Dönem sayısı & std sapma" height={260}>
          <ResponsiveContainer>
            <BarChart data={summaryBars}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="l" dataKey="donem" name="Dönem (N)" fill={C.blue} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="r" dataKey="std" name="Std (m³)" fill={C.orange} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {gpsParks.length > 0 && (
        <ChartCard
          title="15) GPS’li parklar — konum özeti"
          sub="Doğrulanmış lat/lng (grafik: ilçe bazlı ortalama lat referans)"
          height={220}
        >
          <ResponsiveContainer>
            <ScatterChartLike parks={gpsParks} />
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
};

/** Basit lat/lng scatter — recharts ScatterChart wrapper */
function ScatterChartLike({ parks }) {
  const data = parks.map((p, i) => ({
    x: Number(p.location.lng),
    y: Number(p.location.lat),
    name: shortName(p.abone_adi, 20),
    fill: C.palette[i % 5],
  }));
  return (
    <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
      <XAxis
        type="number"
        dataKey="x"
        name="Lng"
        domain={['dataMin - 0.02', 'dataMax + 0.02']}
        tick={{ fontSize: 10 }}
        label={{ value: 'Boylam (lng)', position: 'insideBottom', offset: -2, fontSize: 10 }}
      />
      <YAxis
        type="number"
        dataKey="y"
        name="Lat"
        domain={['dataMin - 0.02', 'dataMax + 0.02']}
        tick={{ fontSize: 10 }}
        label={{ value: 'Enlem', angle: -90, position: 'insideLeft', fontSize: 10 }}
      />
      <Tooltip
        cursor={{ strokeDasharray: '3 3' }}
        formatter={(v, name) => [Number(v).toFixed(6), name === 'x' ? 'Lng' : 'Lat']}
        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
      />
      <Scatter name="Park" data={data}>
        {data.map((e) => <Cell key={e.name} fill={e.fill} />)}
      </Scatter>
    </ScatterChart>
  );
}

export default AdminRaporCharts;
